const JANE_CONNECTION_ERROR_MESSAGE = 'Jane could not finish the response because the connection was interrupted. Please try again.';
const JANE_REQUEST_TIMEOUT_MESSAGE = 'Jane took too long to respond. Please try again.';

const safeResponseText = async (response) => {
    try {
        return String(await response.text()).trim();
    } catch (error) {
        return '';
    }
};

const responseMessageFromText = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || /^\s*</.test(trimmed)) return '';
    try {
        const payload = JSON.parse(trimmed);
        return typeof payload?.message === 'string' ? payload.message.trim() : '';
    } catch (error) {
        return '';
    }
};

export const buildRagTransportFailure = ({ status = 0, message = '', errorCode = 'jane_connection_interrupted' } = {}) => {
    const normalizedStatus = Number(status || 0);
    const safeMessage = String(message || '').trim();
    return {
        success: false,
        error_code: errorCode,
        http_status: Number.isFinite(normalizedStatus) && normalizedStatus > 0 ? normalizedStatus : null,
        retryable: normalizedStatus === 0 || normalizedStatus === 408 || normalizedStatus === 425 || normalizedStatus === 429 || normalizedStatus >= 500,
        message: safeMessage || JANE_CONNECTION_ERROR_MESSAGE,
    };
};

const buildSafeRagRequestError = (message, code, status = 0) => {
    const error = new Error(String(message || JANE_CONNECTION_ERROR_MESSAGE));
    error.code = code;
    error.status = Number(status || 0) || null;
    error.isSafeForDisplay = true;
    return error;
};

const getSafeRagHttpErrorMessage = (status, fallbackMessage) => {
    const normalizedStatus = Number(status || 0);
    if (normalizedStatus === 401) return 'Your CaseDB session expired. Refresh the page and try again.';
    if (normalizedStatus === 403) return 'Your account does not have access to that Jane chat or source.';
    if (normalizedStatus === 429) return 'Jane is handling too many requests right now. Wait a moment and try again.';
    if (normalizedStatus >= 500) return 'Jane is temporarily unavailable. Please try again.';
    return String(fallbackMessage || JANE_CONNECTION_ERROR_MESSAGE);
};

export const fetchRagJsonWithTimeout = async (url, options = {}, requestOptions = {}) => {
    const timeoutMs = Math.max(1000, Number(requestOptions.timeoutMs || 30000));
    const fallbackMessage = String(requestOptions.failureMessage || JANE_CONNECTION_ERROR_MESSAGE);
    const timeoutMessage = String(requestOptions.timeoutMessage || JANE_REQUEST_TIMEOUT_MESSAGE);
    const controller = new AbortController();
    const externalSignal = options?.signal || null;
    let timedOut = false;
    const handleExternalAbort = () => controller.abort();
    externalSignal?.addEventListener?.('abort', handleExternalAbort, { once: true });
    if (externalSignal?.aborted) controller.abort();
    const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);

    try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const responseText = await safeResponseText(response);
        let data = null;
        try {
            data = responseText ? JSON.parse(responseText) : null;
        } catch (error) {
            data = null;
        }
        if (!response.ok) {
            throw buildSafeRagRequestError(
                getSafeRagHttpErrorMessage(response.status, fallbackMessage),
                'jane_http_error',
                response.status
            );
        }
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw buildSafeRagRequestError(fallbackMessage, 'jane_invalid_response', response.status);
        }
        return { response, data };
    } catch (error) {
        if (error?.isSafeForDisplay) throw error;
        if (error?.name === 'AbortError') {
            throw buildSafeRagRequestError(
                timedOut ? timeoutMessage : fallbackMessage,
                timedOut ? 'jane_request_timeout' : 'jane_request_cancelled'
            );
        }
        throw buildSafeRagRequestError(fallbackMessage, 'jane_connection_interrupted');
    } finally {
        clearTimeout(timeoutId);
        externalSignal?.removeEventListener?.('abort', handleExternalAbort);
    }
};

export const readJsonLineStream = async (response, onEvent, signal = null) => {
    if (!response || typeof response !== 'object') {
        return buildRagTransportFailure();
    }
    if (!response.ok) {
        const responseText = await safeResponseText(response);
        return buildRagTransportFailure({
            status: response.status,
            message: responseMessageFromText(responseText),
            errorCode: 'jane_http_error',
        });
    }

    const reader = response.body?.getReader();
    if (!reader) {
        try {
            const payload = await response.json();
            return payload && typeof payload === 'object'
                ? payload
                : buildRagTransportFailure({ errorCode: 'jane_invalid_response' });
        } catch (error) {
            return buildRagTransportFailure({ errorCode: 'jane_invalid_response' });
        }
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let completedPayload = null;
    const createAbortError = () => {
        const error = new Error('Aborted');
        error.name = 'AbortError';
        return error;
    };

    const handleAbort = () => {
        try {
            const cancellation = reader.cancel();
            cancellation?.catch?.(() => {});
        } catch (error) {}
    };

    if (signal?.aborted) {
        handleAbort();
        throw createAbortError();
    }

    signal?.addEventListener?.('abort', handleAbort, { once: true });

    const parseLine = (line) => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        let event = null;
        try {
            event = JSON.parse(trimmedLine);
        } catch (error) {
            return;
        }
        onEvent?.(event);
        if (event.type === 'complete') {
            completedPayload = event.payload && typeof event.payload === 'object'
                ? { ...event.payload }
                : event.payload;
            if (completedPayload && typeof completedPayload === 'object') {
                const elapsedMs = Number(event.elapsed_ms ?? completedPayload.elapsed_ms ?? 0);
                if (Number.isFinite(elapsedMs) && elapsedMs > 0) {
                    completedPayload.elapsed_ms = elapsedMs;
                    if (completedPayload.intelligence && typeof completedPayload.intelligence === 'object') {
                        completedPayload.intelligence = {
                            ...completedPayload.intelligence,
                            response_plan_elapsed_ms: Number(completedPayload.intelligence.response_plan_elapsed_ms || elapsedMs),
                            response_plan_completed_at: completedPayload.intelligence.response_plan_completed_at || event.completed_at || completedPayload.completed_at || null,
                        };
                    }
                }
                if (event.completed_at && !completedPayload.completed_at) {
                    completedPayload.completed_at = event.completed_at;
                }
            }
        } else if (event.success === false) {
            completedPayload = event;
        }
    };

    try {
        while (true) {
            if (signal?.aborted) throw createAbortError();
            const { done, value } = await reader.read();
            // Cancelling a reader commonly resolves the pending read with
            // `done: true`; it does not necessarily reject it. Preserve the
            // caller's abort semantics instead of converting cancellation into
            // a misleading incomplete-stream error.
            if (signal?.aborted) throw createAbortError();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            lines.forEach(parseLine);
        }

        buffer += decoder.decode();
        parseLine(buffer);
        if (signal?.aborted) throw createAbortError();
        return completedPayload || buildRagTransportFailure({ errorCode: 'jane_stream_incomplete' });
    } finally {
        signal?.removeEventListener?.('abort', handleAbort);
        try {
            await reader.cancel();
        } catch (error) {}
    }
};

const getRagStreamChunkWordBudget = (remainingLength) => {
    if (remainingLength > 3200) return 16;
    if (remainingLength > 1800) return 10;
    if (remainingLength > 900) return 6;
    if (remainingLength > 280) return 4;
    return 2;
};

export const getNextRagStreamText = (currentText, targetText) => {
    const safeCurrentText = targetText.startsWith(currentText) ? currentText : '';
    const remainingText = targetText.slice(safeCurrentText.length);
    if (!remainingText) return targetText;

    const tokens = remainingText.match(/\s+|[^\s]+/g) || [remainingText];
    const wordBudget = getRagStreamChunkWordBudget(remainingText.length);
    let nextChunk = '';
    let wordCount = 0;

    for (let index = 0; index < tokens.length; index += 1) {
        const token = tokens[index];
        nextChunk += token;
        if (!/^\s+$/.test(token)) {
            wordCount += 1;
        }
        if (wordCount >= wordBudget) {
            if (index + 1 < tokens.length && /^\s+$/.test(tokens[index + 1])) {
                nextChunk += tokens[index + 1];
            }
            break;
        }
    }

    return safeCurrentText + (nextChunk || remainingText);
};

export const getRagStreamDelay = (visibleText, remainingLength) => {
    if (remainingLength > 2200) return 10;
    if (/\n\s*$/.test(visibleText)) return 70;
    if (/[.!?]["')\]]?\s*$/.test(visibleText)) return 62;
    if (/[,;:]["')\]]?\s*$/.test(visibleText)) return 36;
    if (remainingLength > 900) return 16;
    return 24;
};
